import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getGetBatchQueryOptions } from "@/requests/gen/react-query/batch";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import BatchLogLookupTable from "./-log-lookup-table";
import { DollarSign, Hourglass, Package, Printer } from "lucide-react";
import formatUSD from "@/lib/format-currency";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BatchCrumbs } from "../-crumbs";
import parsePhoneNumber from "libphonenumber-js";
import { useUpdateBatchInvoiceUrl } from "@/requests/gen/react-query/batch";
import { useFirebaseUpload } from "@/lib/use-firebase-upload";
import { Input } from "@/components/ui/input";
import { BatchActions } from "./-batch-actions";
import { BatchStatusActions } from "./-batch-status-actions";
import { toast } from "sonner";
import BatchHistory from "./-batch-history";
import { useAuth } from "@/auth/use-auth";
import numericId from "@/lib/numeric-id";

export const Route = createFileRoute(
  "/_private/_warehouse-360/batch/$batchId/",
)({
  loader: ({ context: { queryClient }, params: { batchId } }) =>
    queryClient.ensureQueryData(getGetBatchQueryOptions(numericId(batchId))),
  component: BatchDetailsPage,
});

function BatchDetailsPage() {
  const { batchId } = Route.useParams();
  const batchIdInt = numericId(batchId);
  const { data: batch, refetch } = useSuspenseQuery(
    suspenseOptions(getGetBatchQueryOptions(batchIdInt)),
  );
  const signedUrl = batch?.invoice_signed_url;
  const { authUser } = useAuth();
  const isAdmin = authUser?.permissions.includes("administrate_legrande");

  const editMutation = useUpdateBatchInvoiceUrl();

  // Firebase storage
  const { selectedFile, setSelectedFile, uploadFileToBucket } =
    useFirebaseUpload("batch_invoices");

  return (
    <div className="flex flex-col">
      <title>{`Batch ${batchId} - Legrande Health Portal`}</title>
      <div className="flex flex-row justify-between items-center">
        <BatchCrumbs
          warehouseId={batch?.warehouse_vendor.warehouse_id.toString()}
        />
        <div className="flex items-center gap-2">
          {batch && <BatchActions batch={batch} />}
        </div>
      </div>
      <Card className="mb-4">
        <CardHeader className="border-b pb-4!">
          <CardTitle className="text-lg flex items-center gap-2">
            Batch #{batch?.external_batch_id || batch?.batch_id}
            {batch && (
              <Badge
                variant={
                  batch.status === "Arrived"
                    ? "default"
                    : batch.status === "Cancelled"
                      ? "destructive"
                      : "secondary"
                }
              >
                {batch.status}
              </Badge>
            )}
          </CardTitle>
          {batch &&
            batch.status !== "Cancelled" &&
            (batch.status !== "Arrived" ||
              (isAdmin && batch.batch_logs.length <= 1)) && (
              <CardAction className="flex gap-4 items-center">
                <Button variant="link" className="p-0 h-4" asChild>
                  <Link
                    to="/batch/$batchId/edit"
                    params={{ batchId: String(batch.batch_id) }}
                    search={{
                      warehouseId: batch.warehouse_vendor.warehouse_id,
                    }}
                  >
                    Edit Batch
                  </Link>
                </Button>
                {batch.status !== "Arrived" && (
                  <BatchStatusActions batch={batch} />
                )}
              </CardAction>
            )}
        </CardHeader>
        <CardContent>
          <Link
            to="/warehouse/$warehouseId"
            params={{
              warehouseId: String(batch?.warehouse_vendor.warehouse_id),
            }}
            className="hover:underline underline-offset-2"
          >
            {batch?.warehouse_vendor.warehouse.catalog_product.display_name}
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="px-3 py-1 text-sm my-4">
              Order Qty: {batch?.order_quantity}
            </Badge>
            <Badge
              variant={
                (batch?.quantity_on_hand ?? 0) < 0 ? "destructive" : "default"
              }
              className="px-3 py-1 text-sm my-4"
            >
              In Stock: {batch?.quantity_on_hand}
            </Badge>
            <Badge
              variant={
                (batch?.warehouse_vendor.warehouse.quantity_on_hand ?? 0) <= 0
                  ? "destructive"
                  : "default"
              }
              className="px-3 py-1 text-sm my-4"
            >
              Warehouse Qty:{" "}
              {batch?.warehouse_vendor.warehouse.quantity_on_hand}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 space-y-2">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Package className="size-5 text-primary" />
                <h3 className="font-semibold">Information</h3>
              </div>
              <div className="flex flex-col gap-2 pl-7">
                <p>
                  <span className="text-muted-foreground pr-2">Vendor:</span>
                  <Button variant="link" className="p-0 h-6 text-base" asChild>
                    <Link
                      to="/vendor/$vendorId"
                      params={{
                        vendorId: String(batch?.warehouse_vendor.vendor_id),
                      }}
                    >
                      {batch?.warehouse_vendor.vendor?.name}
                    </Link>
                  </Button>
                </p>
                <p>
                  <span className="text-muted-foreground pr-2">Phone:</span>
                  <span className="font-medium">
                    {parsePhoneNumber(
                      batch?.warehouse_vendor.vendor?.phone ?? "",
                    )?.formatNational()}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">
                    Tracking Number:
                  </span>
                  <br />
                  <span className="font-medium">
                    {batch?.tracking_number || "N/A"}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Owner:</span>
                  <span>{batch?.owner?.name}</span>
                </p>

                {batch?.stock_order_id && (
                  <p>
                    <span className="text-muted-foreground pr-2">
                      Stock Order:
                    </span>
                    <Button
                      variant="link"
                      className="p-0 h-6 text-base"
                      asChild
                    >
                      <Link
                        to="/stock-order/$stockOrderId"
                        params={{
                          stockOrderId: String(batch.stock_order_id),
                        }}
                      >
                        #{batch.stock_order_id}
                      </Link>
                    </Button>
                  </p>
                )}
              </div>
            </div>

            {/* Key Dates */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Hourglass className="size-5 text-primary" />
                <h3 className="font-semibold">Key Dates</h3>
              </div>
              <div className="flex flex-col gap-2 pl-7">
                <p>
                  <span className="text-muted-foreground pr-2">Ordered:</span>
                  <span className="font-medium">
                    {batch?.ordered_at
                      ? format(batch?.ordered_at, "MM-dd-yyyy")
                      : "N/A"}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Expected:</span>
                  <span className="font-medium">
                    {batch?.expected_at
                      ? format(batch?.expected_at, "MM-dd-yyyy")
                      : "N/A"}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Received:</span>
                  <span className="font-medium">
                    {batch?.arrived_at
                      ? format(batch?.arrived_at, "MM-dd-yyyy")
                      : "N/A"}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Expires:</span>
                  <span className="font-medium">
                    {batch?.expiration_date
                      ? format(batch?.expiration_date, "MM-dd-yyyy")
                      : "N/A"}
                  </span>
                </p>
              </div>
            </div>

            {/* Price */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <DollarSign className="size-5 text-primary" />
                <h3 className="font-semibold">Price</h3>
              </div>
              <div className="flex flex-col gap-2 pl-7">
                <p>
                  <span className="text-muted-foreground pr-2">Unit Cost:</span>
                  <span className="font-medium">
                    {formatUSD(batch?.unit_cost)}
                  </span>
                </p>

                <hr className="my-1 w-3/4" />

                <p>
                  <span className="text-muted-foreground pr-2">Subtotal:</span>
                  <span className="font-medium">
                    {formatUSD(batch?.subtotal)}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Shipping:</span>
                  <span className="font-medium">
                    {formatUSD(batch?.shipping_charge)}
                  </span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">Tax:</span>
                  <span className="font-medium">{formatUSD(batch?.tax)}</span>
                </p>

                <p>
                  <span className="text-muted-foreground pr-2">
                    Total Price:
                  </span>
                  <span className="font-medium">
                    {formatUSD(batch?.total_price)}
                  </span>
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Printer className="size-5 text-primary" />
                Invoice
              </h3>

              {signedUrl ? (
                <Badge>Invoice available</Badge>
              ) : (
                <Badge variant="outline">No invoice available</Badge>
              )}

              <Input
                id="invoice-file"
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  if (event.target.files?.[0]) {
                    setSelectedFile(event.target.files[0]);
                  }
                }}
              />

              <div className="flex w-full gap-4">
                <Button
                  variant="default"
                  className="flex-1"
                  disabled={!selectedFile || editMutation.isPending}
                  onClick={async () => {
                    if (!batch || !selectedFile) return;
                    try {
                      const invoiceUrl = await uploadFileToBucket(
                        batch.invoice_url || undefined,
                      );

                      await editMutation.mutateAsync({
                        batchId: batch.batch_id,
                        params: {
                          invoice_url: invoiceUrl,
                        },
                      });
                      setSelectedFile(undefined);
                      await refetch();
                      toast.success("Invoice uploaded successfully.");
                    } catch {
                      toast.error("Invoice upload failed.");
                    }
                  }}
                >
                  Upload Invoice
                </Button>

                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={!signedUrl}
                  asChild={!!signedUrl}
                >
                  {signedUrl ? (
                    <Link
                      to={signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Invoice
                    </Link>
                  ) : (
                    <span>View Invoice</span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BatchLogLookupTable batch={batch} refetch={refetch} />

      <div className="mt-4">
        <BatchHistory batch={batch} />
      </div>
    </div>
  );
}
